import { toPng } from 'html-to-image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientPackageDto } from '../api/generated/client';
import { getRequiredChartExporterDomChapterNames } from '../config/client-package-chart-exporter';
import { CLIENT_OUTPUT_EVENT_YEAR_0_ID, CLIENT_OUTPUT_EVENT_YEAR_1_ID, CLIENT_OUTPUT_EVENT_YEAR_2_ID } from '../config/constants';
import { ClientPackageChapterName } from '../enums';
import type { ClientPackageRenderingEvent, IChapterImageDom } from '../interfaces/package';
import { getSplitClientPackageChapter } from '../utils/clientPackageUtils';
import { captureFamilyTreeViewportToPng } from '../utils/family-tree-image-capture';
import { showErrorToast } from '../utils/toastUtils';
import { getYwuShadowRoot } from '../utils/utils';
import { waitUntil } from '../utils/wait-for-dom-registration';
import { wnSendTrace } from '../utils/wn-event-utils';

const POST_DOM_SETTLE_DELAY_MS = 500;
const BETWEEN_CHAPTER_CAPTURE_DELAY_MS = 100;
const FAMILY_TREE_RETRY_DELAY_MS = 400;

const POLL_INTERVAL_MS = 100;
const MAX_POLL_ATTEMPTS = Math.ceil(15000 / POLL_INTERVAL_MS);

interface IImageRenderingState {
    event?: ClientPackageRenderingEvent;
    isRendering: boolean;
}

type ImageDom = { dom: HTMLElement; options?: Parameters<typeof toPng>[1] };

interface IImageDomes {
    [ClientPackageChapterName.CASHFLOW]?: ImageDom;
    [ClientPackageChapterName.CASHFLOW2]?: ImageDom;
    [ClientPackageChapterName.EVENTS_0]?: ImageDom;
    [ClientPackageChapterName.EVENTS_1]?: ImageDom;
    [ClientPackageChapterName.EVENTS_2]?: ImageDom;
    [ClientPackageChapterName.PROJECTION]?: ImageDom;
    [ClientPackageChapterName.FAMILY_TREE]?: ImageDom;
    [ClientPackageChapterName.LIQUIDITY]?: ImageDom;
}

export interface IUseClientPackageImageRenderingOptions {
    projectionYears: number;
    partnerUuid?: string;
}

export function useClientPackageImageRendering(
    clientPackage: ClientPackageDto,
    updateClientPackage: (updatedPackage: Partial<ClientPackageDto>) => void,
    { projectionYears, partnerUuid = '' }: IUseClientPackageImageRenderingOptions,
) {
    const { t } = useTranslation();
    const [isImageRendering, setIsImageRenderingInProcess] = useState<IImageRenderingState>({
        event: undefined,
        isRendering: false,
    });
    const [imageDomes, setImageDomes] = useState<IImageDomes>({
        [ClientPackageChapterName.CASHFLOW]: undefined,
        [ClientPackageChapterName.CASHFLOW2]: undefined,
        [ClientPackageChapterName.EVENTS_0]: undefined,
        [ClientPackageChapterName.EVENTS_1]: undefined,
        [ClientPackageChapterName.EVENTS_2]: undefined,
        [ClientPackageChapterName.PROJECTION]: undefined,
        [ClientPackageChapterName.FAMILY_TREE]: undefined,
        [ClientPackageChapterName.LIQUIDITY]: undefined,
    });

    const imageDomesRef = useRef(imageDomes);
    useEffect(() => {
        imageDomesRef.current = imageDomes;
    }, [imageDomes]);

    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const addImageRenderingDom = useCallback(
        (chapterImageDom: IChapterImageDom) => {
            setImageDomes((prevImageDomes) => ({
                ...prevImageDomes,
                [chapterImageDom.chapterName]: { dom: chapterImageDom.dom, options: chapterImageDom.options },
            }));
        },
        [setImageDomes],
    );

    const convertDomToImagePromises = (domes: IImageDomes) => {
        const imagePromises: Record<string, Promise<string> | undefined> = {};

        Object.entries(domes).forEach(([chapterName, imageDom]) => {
            if (!imageDom) return;

            if (chapterName === ClientPackageChapterName.CASHFLOW2) {
                imagePromises[chapterName] = toPng(imageDom.dom, imageDom.options);

                return;
            }
            if (chapterName.includes(ClientPackageChapterName.EVENTS_0)) {
                const htmlDocument = getYwuShadowRoot() ?? document;
                const elementYear0 = htmlDocument.querySelector<HTMLElement>(`#${CLIENT_OUTPUT_EVENT_YEAR_0_ID}`);
                const elementYear1 = htmlDocument.querySelector<HTMLElement>(`#${CLIENT_OUTPUT_EVENT_YEAR_1_ID}`);
                const elementYear2 = htmlDocument.querySelector<HTMLElement>(`#${CLIENT_OUTPUT_EVENT_YEAR_2_ID}`);

                if (elementYear0) imagePromises[ClientPackageChapterName.EVENTS_0] = toPng(elementYear0);
                if (elementYear1) imagePromises[ClientPackageChapterName.EVENTS_1] = toPng(elementYear1);
                if (elementYear2) imagePromises[ClientPackageChapterName.EVENTS_2] = toPng(elementYear2);
            } else if (chapterName === ClientPackageChapterName.FAMILY_TREE) {
                imagePromises[chapterName] = captureFamilyTreeViewportToPng(imageDom.dom, imageDom.options);
            } else {
                imagePromises[chapterName] = toPng(imageDom.dom, imageDom.options);
            }
        });

        return imagePromises;
    };

    const captureChapterImage = async (chapterName: string, chapterPromise: Promise<string>): Promise<string> => {
        try {
            return await chapterPromise;
        } catch (firstErr) {
            if (chapterName !== ClientPackageChapterName.FAMILY_TREE) throw firstErr;

            await new Promise((r) => setTimeout(r, FAMILY_TREE_RETRY_DELAY_MS));
            const familyDom = imageDomesRef.current[ClientPackageChapterName.FAMILY_TREE];
            if (!familyDom) throw firstErr;

            try {
                return await captureFamilyTreeViewportToPng(familyDom.dom, familyDom.options);
            } catch (retryErr) {
                console.error(`Family tree client-output capture failed after retry for chapter "${chapterName}"`, retryErr, { cause: firstErr });
                throw retryErr;
            }
        }
    };

    const setImagesForRenderingEvent = async (event: ClientPackageRenderingEvent) => {
        setIsImageRenderingInProcess({ event, isRendering: true });

        const requiredChapterNames = getRequiredChartExporterDomChapterNames(clientPackage.clientPackageChapters, projectionYears);

        await waitUntil(
            () => {
                const current = imageDomesRef.current;

                return requiredChapterNames.every((name) => !!current[name as keyof IImageDomes]);
            },
            { intervalMs: POLL_INTERVAL_MS, maxAttempts: MAX_POLL_ATTEMPTS },
        );

        await new Promise((r) => setTimeout(r, POST_DOM_SETTLE_DELAY_MS));

        const currentDomes = imageDomesRef.current;
        const imagePromises = convertDomToImagePromises(currentDomes);

        const failedChapters: string[] = [];

        for (const [chapterName, chapterPromise] of Object.entries(imagePromises)) {
            if (!chapterPromise) continue;

            try {
                const image = await captureChapterImage(chapterName, chapterPromise);
                const { chapter, chapters } = getSplitClientPackageChapter(clientPackage, chapterName);
                if (!chapter) continue;

                switch (chapterName) {
                    case ClientPackageChapterName.EVENTS_0:
                        chapter.lifeEventImages = [image];
                        break;
                    case ClientPackageChapterName.EVENTS_1:
                        chapter.lifeEventImages?.push(image);
                        break;
                    case ClientPackageChapterName.EVENTS_2:
                        chapter.lifeEventImages?.push(image);
                        break;
                    case ClientPackageChapterName.PROJECTION:
                        chapter.projectionImage = image;
                        break;
                    case ClientPackageChapterName.CASHFLOW:
                        chapter.cashflowImage = image;
                        break;
                    case ClientPackageChapterName.CASHFLOW2:
                        chapter.cashflowImage2 = image;
                        break;
                    case ClientPackageChapterName.FAMILY_TREE:
                        chapter.familyTreeImage = image;
                        break;
                    case ClientPackageChapterName.LIQUIDITY:
                        chapter.liquidityImage = image;
                        break;
                }

                if (isMountedRef.current) {
                    updateClientPackage({ clientPackageChapters: [...chapters, chapter] });
                }

                await new Promise((r) => setTimeout(r, BETWEEN_CHAPTER_CAPTURE_DELAY_MS));
            } catch (err) {
                failedChapters.push(chapterName);
                console.error(`Failed to render the chapter "${chapterName}" for the client-package output`, err);
            }
        }

        if (failedChapters.length > 0 && isMountedRef.current) {
            showErrorToast({
                userMessage: t('clientPackageChapterImagesPartialFailure'),
                error: { failedChapters, event, clientPackageId: clientPackage.id },
            });
            if (partnerUuid) {
                wnSendTrace({
                    feature: 'client-package',
                    tags: {
                        partnerUuid,
                        action: 'client-package-chapter-image-render-failed',
                        clientPackageId: `${clientPackage.id ?? ''}`,
                        chapters: failedChapters.join(','),
                        event,
                    },
                });
            }
        }

        if (isMountedRef.current) {
            setIsImageRenderingInProcess({ event, isRendering: false });
        }
    };

    return { isImageRendering, addImageRenderingDom, setImagesForRenderingEvent };
}
